import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { getPaymentStatus } from "../lib/api";

export default function PaymentResult({ mode }) {
    const [searchParams] = useSearchParams();
    const [state, setState] = useState(mode === "success" ? "verifying" : "cancelled");

    useEffect(() => {
        if (mode !== "success") return;
        const sid = searchParams.get("session_id");
        if (!sid) {
            setState("missing");
            return;
        }
        let tries = 0;
        let timer;
        const tick = async () => {
            try {
                const r = await getPaymentStatus(sid);
                if (r.payment_status === "paid") {
                    setState("paid");
                    return;
                }
            } catch (e) {
                /* keep polling */
            }
            tries += 1;
            if (tries < 15) timer = setTimeout(tick, 2000);
            else setState("pending");
        };
        tick();
        return () => clearTimeout(timer);
    }, [mode, searchParams]);

    return (
        <div className="bg-paper text-ink min-h-screen flex items-center justify-center px-5" data-testid="payment-result-page">
            <div className="max-w-md w-full border border-ink/10 bg-white p-10 text-center">
                {mode === "cancel" ? (
                    <>
                        <XCircle size={44} className="text-ink/40 mx-auto" />
                        <h1 className="mt-5 font-display font-semibold text-3xl tracking-tight">Payment cancelled</h1>
                        <p className="mt-3 text-ink/60 text-sm leading-relaxed" data-testid="payment-cancel-text">
                            No charge was made. If you already booked, your session still stands — you can pay
                            after class via CashApp or cash.
                        </p>
                    </>
                ) : state === "verifying" ? (
                    <>
                        <Loader2 size={44} className="text-worx mx-auto animate-spin" />
                        <h1 className="mt-5 font-display font-semibold text-3xl tracking-tight">Confirming payment…</h1>
                        <p className="mt-3 text-ink/60 text-sm" data-testid="payment-verifying-text">One moment while we check with Stripe.</p>
                    </>
                ) : state === "paid" ? (
                    <>
                        <CheckCircle2 size={44} className="text-worx mx-auto" />
                        <h1 className="mt-5 font-display font-semibold text-3xl tracking-tight">Payment confirmed</h1>
                        <p className="mt-3 text-ink/60 text-sm leading-relaxed" data-testid="payment-success-text">
                            You're all set. A receipt from Stripe is on its way to your inbox.
                        </p>
                    </>
                ) : (
                    <>
                        <Loader2 size={44} className="text-ink/40 mx-auto" />
                        <h1 className="mt-5 font-display font-semibold text-3xl tracking-tight">Still processing</h1>
                        <p className="mt-3 text-ink/60 text-sm leading-relaxed" data-testid="payment-pending-text">
                            Your payment is being processed. If you were charged, you're confirmed — check your email.
                        </p>
                    </>
                )}
                <Link
                    to="/"
                    data-testid="payment-home-link"
                    className="mt-8 inline-block bg-ink text-paper px-6 py-3 text-sm hover:bg-worx transition-colors"
                >
                    Back to Consulting WorX
                </Link>
            </div>
        </div>
    );
}
